import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconSearch } from '@tabler/icons-react';
import Popover from '../Popover';
import { FONT_CATALOG, FONT_CATEGORIES, cssFamily, loadFontSheet } from '../../../model/fonts';
import './styles.scss';

const normalize = value => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Each family is previewed in its own face (its sheet loads when the list opens); brand
// fonts lead the list.
function FontList({ value, brand = [], onPick, close }) {
  const { t } = useTranslation('artEditor');
  const [query, setQuery] = useState('');
  useEffect(() => { FONT_CATALOG.forEach(entry => loadFontSheet(entry.family)); }, []);
  const groups = useMemo(() => {
    const match = entry => !query.trim() || normalize(entry.family).includes(normalize(query.trim()));
    const brandFamilies = [...new Set(brand)].filter(family => FONT_CATALOG.some(entry => entry.family === family));
    const list = [];
    if (brandFamilies.length && !query.trim()) list.push({ key: 'brand', items: FONT_CATALOG.filter(entry => brandFamilies.includes(entry.family)) });
    FONT_CATEGORIES.forEach(category => {
      const items = FONT_CATALOG.filter(entry => entry.category === category && match(entry));
      if (items.length) list.push({ key: category, items });
    });
    return list;
  }, [query, brand]);
  return <div className="art-fonts">
    <label className="art-fonts__search">
      <IconSearch aria-hidden="true" />
      <input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder={t('fontSearch')} aria-label={t('fontSearch')} />
    </label>
    <div className="art-fonts__list" role="listbox" aria-label={t('font')}>
      {groups.map(group => <div key={group.key} className="art-fonts__group">
        <Text.caption contrast={6}>{t(`fontGroup_${group.key}`)}</Text.caption>
        {group.items.map(entry => <button key={`${group.key}-${entry.family}`} type="button" role="option" aria-selected={entry.family === value}
          className={`art-fonts__option ${entry.family === value ? 'is-active' : ''}`} style={{ '--art-font': cssFamily(entry.family) }}
          onClick={() => { onPick(entry.family); close?.(); }}>
          <span className="art-fonts__sample">{entry.family}</span>
        </button>)}
      </div>)}
      {!groups.length && <Text.caption contrast={6}>{t('fontEmpty')}</Text.caption>}
    </div>
  </div>;
}

export default function FontPicker({ value, brand, onPick, placement = 'top', dock = false }) {
  const { t } = useTranslation('artEditor');
  return <Popover label={t('font')} placement={placement} width={260} dock={dock} trigger={
    <Button className="art-font-trigger" size="small" variant="text" color="overlay" dropdown preventLoading aria-label={`${t('font')}: ${value}`}>
      <span className="art-font-trigger__name" style={{ '--art-font': cssFamily(value) }}>{value}</span>
    </Button>}>
    {({ close }) => <FontList value={value} brand={brand} onPick={onPick} close={close} />}
  </Popover>;
}
